import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, ListChecks, ShieldX, ShieldCheck, Users, Crown, Building, UserCheck } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface InterviewSection {
  title: string;
  duration: string;
  questions: string[];
  redFlags: string[];
  greenFlags: string[];
}

interface InterviewPlanProps {
  plan?: string;
  perspectives?: Record<string, string>;
  initialPerspective?: InterviewerPerspective;
  analysisId?: string;
  onPerspectiveChange?: (perspective: InterviewerPerspective) => void;
  onTransformedSectionsChange?: (sections: InterviewSection[]) => void;
}

type InterviewerPerspective = 'hr' | 'manager' | 'senior-manager' | 'c-level' | 'technical-lead';

const perspectiveConfig = {
  'hr': {
    label: 'HR Manager',
    icon: Users,
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    description: 'Cultural fit, policy compliance, compensation discussions',
    focus: ['Cultural alignment', 'Team dynamics', 'Communication skills', 'Work-life balance', 'Career development']
  },
  'manager': {
    label: 'Direct Manager',
    icon: UserCheck,
    color: 'bg-green-100 text-green-800 border-green-200',
    description: 'Day-to-day performance, task execution, team collaboration',
    focus: ['Project execution', 'Problem-solving approach', 'Team collaboration', 'Daily responsibilities', 'Goal setting']
  },
  'senior-manager': {
    label: 'Senior Manager',
    icon: Building,
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    description: 'Strategic thinking, leadership potential, cross-functional impact',
    focus: ['Strategic thinking', 'Leadership potential', 'Cross-functional collaboration', 'Process improvement', 'Mentoring abilities']
  },
  'c-level': {
    label: 'C-Level Executive',
    icon: Crown,
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    description: 'Vision alignment, business impact, organizational growth',
    focus: ['Business vision', 'Market understanding', 'Innovation potential', 'Organizational impact', 'Long-term thinking']
  },
  'technical-lead': {
    label: 'Technical Lead',
    icon: ListChecks,
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    description: 'Technical depth, architecture decisions, code quality standards',
    focus: ['Technical expertise', 'Architecture decisions', 'Code quality', 'Technology trends', 'Technical mentoring']
  }
};

export function InterviewPlan({ 
  plan, 
  perspectives, 
  initialPerspective = 'manager',
  analysisId,
  onPerspectiveChange, 
  onTransformedSectionsChange 
}: InterviewPlanProps) {
  const [selectedPerspective, setSelectedPerspective] = useState<InterviewerPerspective>(initialPerspective);
  const [transformedSections, setTransformedSections] = useState<InterviewSection[]>([]);

  // Debug: Log what we receive
  console.log('🎯 InterviewPlan received:', {
    planLength: plan?.length || 0,
    perspectivesKeys: Object.keys(perspectives || {}),
    initialPerspective,
    analysisId
  });

  const parseSections = (planText: string | undefined): InterviewSection[] => {
    if (!planText) {
      return [];
    }

    try {
      console.log('Raw interview plan text:', planText); // Debug logging
      
      // Enhanced REGEX pattern to handle various formats and better section boundaries
      const sectionPattern = /(\d+)\.\s*Question Category:\s*\[?\s*([^\[\]\(\)\n]+?)\s*\]?\s*\(?\s*(\d+(?:-\d+)?)\s*minutes?\)?\s*([\s\S]*?)(?=(?:\d+\.\s*Question Category:|$))/gi;
      const sections: InterviewSection[] = [];
      
      let match;
      while ((match = sectionPattern.exec(planText)) !== null) {
        console.log('Matched section:', match); // Debug logging
        const [, , title, duration, content] = match;
        
        // Initialize arrays for this section
        const questions: string[] = [];
        const redFlags: string[] = [];
        const greenFlags: string[] = [];
        
        // Check if this is the new simple format (just numbered questions)
        const numberedQuestionPattern = /^\s*(\d+)\.\s*(.+)$/gm;
        const hasNumberedQuestions = content.match(numberedQuestionPattern);
        
        if (hasNumberedQuestions) {
          // NEW FORMAT: Extract numbered questions directly
          console.log('🆕 Parsing new simple format for:', title);
          
          // Reset the regex to start from the beginning
          numberedQuestionPattern.lastIndex = 0;
          
          let questionMatch;
          while ((questionMatch = numberedQuestionPattern.exec(content)) !== null) {
            const questionText = questionMatch[2].trim();
            // Filter out section headers and focus on actual questions
            if (questionText && 
                questionText.length > 10 && 
                !questionText.toLowerCase().includes('question category') &&
                !questionText.toLowerCase().includes('minutes total')) {
              questions.push(questionText);
              console.log(`✅ Added question: ${questionText.substring(0, 50)}...`);
            }
          }
          
          // Extract red flags in new format
          const newRedFlagPatterns = [
            /Red Flags?\s*:\s*([\s\S]*?)(?=Green Flags?:|Question Category:|\d+\.\s*Question Category:|$)/i
          ];
          
          console.log(`🔍 Looking for red flags in new format for: ${title}`);
          for (const pattern of newRedFlagPatterns) {
            const redFlagMatch = content.match(pattern);
            if (redFlagMatch) {
              console.log(`🚩 Red flag match found:`, redFlagMatch[1].substring(0, 100));
              const redFlagContent = redFlagMatch[1].trim();
              
              const redFlagLines = redFlagContent.split('\n')
                .map(line => line.trim())
                .filter(line => line && line.startsWith('-'))
                .map(line => line.replace(/^-\s*/, '').trim())
                .filter(line => line.length > 5);
              
              redFlags.push(...redFlagLines);
              console.log(`✅ Added ${redFlagLines.length} red flags for ${title}`);
              break;
            }
          }
          
          // Extract green flags in new format
          const newGreenFlagPatterns = [
            /Green Flags?\s*:\s*([\s\S]*?)(?=Question Category:|\d+\.\s*Question Category:|$)/i
          ];
          
          console.log(`🔍 Looking for green flags in new format for: ${title}`);
          for (const pattern of newGreenFlagPatterns) {
            const greenFlagMatch = content.match(pattern);
            if (greenFlagMatch) {
              console.log(`🟢 Green flag match found:`, greenFlagMatch[1].substring(0, 100));
              const greenFlagContent = greenFlagMatch[1].trim();
              
              const greenFlagLines = greenFlagContent.split('\n')
                .map(line => line.trim())
                .filter(line => line && line.startsWith('-'))
                .map(line => line.replace(/^-\s*/, '').trim())
                .filter(line => line.length > 5);
              
              greenFlags.push(...greenFlagLines);
              console.log(`✅ Added ${greenFlagLines.length} green flags for ${title}`);
              break;
            }
          }
        } else {
          // OLD FORMAT: Extract primary question and follow-ups
          console.log('📄 Parsing old format for:', title);
          
          // Extract primary question - more flexible patterns
          const primaryQuestionPatterns = [
            /Primary Question:\s*(.*?)(?=\s*\n|\s*Expected Answer:|\s*Follow-up Questions:)/is,
            /Primary Question:\s*(.*?)(?=\s*Expected Answer:|\s*Follow-up Questions:|\s*Red Flags:)/is,
            /Primary Question:\s*([\s\S]*?)(?=Expected Answer:|Follow-up Questions:|Red Flags:|Green Flags:|Evaluation Criteria:|\d+\.|$)/i
          ];
          
          let primaryQuestion = null;
          for (const pattern of primaryQuestionPatterns) {
            const match = content.match(pattern);
            if (match && match[1].trim()) {
              primaryQuestion = match[1].trim();
              break;
            }
          }
          
          if (primaryQuestion) {
            questions.push(primaryQuestion);
          }
          
          // Extract follow-up questions - enhanced to handle conditional follow-ups
          const followUpPatterns = [
            /Follow-up Questions:\s*\[CONDITIONAL[^\]]*\]\s*([\s\S]*?)(?=Red Flags:|Green Flags:|Expected Answer:|Evaluation Criteria:|\d+\.|$)/i,
            /Follow-up Questions:\s*([\s\S]*?)(?=Red Flags:|Green Flags:|Expected Answer:|Evaluation Criteria:|\d+\.|$)/i,
            /Follow-up Questions:([\s\S]*?)(?=Red Flags:|Green Flags:|Expected Answer:|Evaluation Criteria:|Primary Question:|\d+\.|$)/i
          ];
          
          for (const pattern of followUpPatterns) {
            const followUpMatch = content.match(pattern);
            if (followUpMatch) {
              const followUpContent = followUpMatch[1].trim();
              
              // Check if follow-ups are intentionally omitted or empty
              if (followUpContent.includes('None needed') || 
                  followUpContent.includes('Not applicable') ||
                  followUpContent.includes('ONLY if needed') ||
                  followUpContent.length < 10) {
                // Skip follow-ups - they're not needed for this question
                break;
              }
              
              const followUpLines = followUpContent.split('\n')
                .map(line => line.trim())
                .filter(line => line.startsWith('-') || line.match(/^\d+\./))
                .filter(line => !line.toLowerCase().includes('only if needed'));
              
              followUpLines.forEach(line => {
                const questionText = line.replace(/^(-\s*|\d+\.\s*)/, '').trim();
                // Enhanced filtering for better quality follow-ups
                if (questionText && 
                    questionText.length > 15 && 
                    !questionText.toLowerCase().includes('conditional') &&
                    !questionText.toLowerCase().includes('only if needed')) {
                  questions.push(questionText);
                }
              });
              break;
            }
          }
          
          // Extract red flags - enhanced patterns and debug logging
          const redFlagPatterns = [
            /Red Flags?\s*:\s*([\s\S]*?)(?=Green Flags?:|Expected Answer:|Evaluation Criteria:|\d+\.\s*Question Category:|$)/i,
            /Red Flags?([\s\S]*?)(?=Green Flags?:|Expected Answer:|Evaluation Criteria:|\d+\.\s*Question Category:|$)/i
          ];
          
          console.log(`Looking for red flags in section: ${title}`);
          for (const pattern of redFlagPatterns) {
            const redFlagMatch = content.match(pattern);
            if (redFlagMatch) {
              console.log(`Red flag match found:`, redFlagMatch[1].substring(0, 100));
              const redFlagContent = redFlagMatch[1].trim();
              
              // More flexible line parsing
              const redFlagLines = redFlagContent.split('\n')
                .map(line => line.trim())
                .filter(line => line && (line.startsWith('-') || line.match(/^\d+\./) || line.length > 10));
              
              redFlagLines.forEach(line => {
                let flagText = line.replace(/^(-\s*|\d+\.\s*)/, '').trim();
                if (flagText && flagText.length > 5 && !flagText.toLowerCase().includes('question category')) {
                  redFlags.push(flagText);
                  console.log(`Added red flag: ${flagText}`);
                }
              });
              break;
            }
          }
          
          // Extract green flags - enhanced patterns and debug logging  
          const greenFlagPatterns = [
            /Green Flags?\s*:\s*([\s\S]*?)(?=Expected Answer:|Evaluation Criteria:|\d+\.\s*Question Category:|$)/i,
            /Green Flags?([\s\S]*?)(?=Expected Answer:|Evaluation Criteria:|\d+\.\s*Question Category:|$)/i
          ];
          
          console.log(`Looking for green flags in section: ${title}`);
          for (const pattern of greenFlagPatterns) {
            const greenFlagMatch = content.match(pattern);
            if (greenFlagMatch) {
              console.log(`Green flag match found:`, greenFlagMatch[1].substring(0, 100));
              const greenFlagContent = greenFlagMatch[1].trim();
              
              // More flexible line parsing
              const greenFlagLines = greenFlagContent.split('\n')
                .map(line => line.trim())
                .filter(line => line && (line.startsWith('-') || line.match(/^\d+\./) || line.length > 10));
              
              greenFlagLines.forEach(line => {
                let flagText = line.replace(/^(-\s*|\d+\.\s*)/, '').trim();
                if (flagText && flagText.length > 5 && !flagText.toLowerCase().includes('question category')) {
                  greenFlags.push(flagText);
                  console.log(`Added green flag: ${flagText}`);
                }
              });
              break;
            }
          }
        }
        
        // Clean up duration (handle ranges like "15-30")
        const cleanDuration = duration.includes('-') ? duration.split('-')[0] : duration;
        
        sections.push({
          title: title.trim(),
          duration: `${cleanDuration} minutes`,
          questions,
          redFlags,
          greenFlags
        });
      }
      
      console.log('Parsed sections:', sections); // Debug logging
      
      // Debug: Log all parsed sections before grouping
      console.log('All parsed sections before grouping:');
      sections.forEach((section, index) => {
        console.log(`Section ${index}:`, {
          title: section.title,
          questionsCount: section.questions.length,
          redFlagsCount: section.redFlags.length,
          greenFlagsCount: section.greenFlags.length,
          redFlags: section.redFlags,
          greenFlags: section.greenFlags
        });
      });

      // Group duplicate categories and merge their content
      const groupedSections = sections.reduce((acc, section) => {
        const existingSection = acc.find(s => s.title.toLowerCase() === section.title.toLowerCase());
        
        if (existingSection) {
          console.log(`Merging duplicate section: ${section.title}`);
          
          // Merge questions, avoiding duplicates
          const newQuestions = section.questions.filter(q => !existingSection.questions.includes(q));
          existingSection.questions.push(...newQuestions);
          
          // Merge flags, avoiding duplicates
          const newRedFlags = section.redFlags.filter(f => !existingSection.redFlags.includes(f));
          existingSection.redFlags.push(...newRedFlags);
          console.log(`Added ${newRedFlags.length} new red flags to ${section.title}`);
          
          const newGreenFlags = section.greenFlags.filter(f => !existingSection.greenFlags.includes(f));
          existingSection.greenFlags.push(...newGreenFlags);
          console.log(`Added ${newGreenFlags.length} new green flags to ${section.title}`);
          
          // Update duration to reflect combined time
          const currentDuration = parseInt(existingSection.duration.match(/\d+/)?.[0] || '20');
          const additionalDuration = parseInt(section.duration.match(/\d+/)?.[0] || '20');
          existingSection.duration = `${currentDuration + additionalDuration} minutes`;
        } else {
          console.log(`Adding new section: ${section.title} with ${section.redFlags.length} red flags and ${section.greenFlags.length} green flags`);
          acc.push(section);
        }
        
        return acc;
      }, [] as InterviewSection[]);
      
      console.log('Grouped sections:', groupedSections); // Debug logging
      return groupedSections;
    } catch (error) {
      console.error('Error parsing interview plan:', error);
      console.error('Plan text that failed:', planText); // Debug logging
      return [];
    }
  };

  // Save perspective preference to database
  const savePerspectivePreference = async (perspective: InterviewerPerspective) => {
    if (!analysisId) return;
    
    try {
      await supabase
        .from('analysis')
        .update({ selected_perspective: perspective })
        .eq('id', analysisId);
    } catch (error) {
      console.error('Failed to save perspective preference:', error);
    }
  };

  const currentConfig = perspectiveConfig[selectedPerspective];
  const Icon = currentConfig.icon;

  // Use stored perspectives instead of AI transformation
  useEffect(() => {
    console.log('🔄 InterviewPlan useEffect triggered:', {
      selectedPerspective,
      hasPerspectives: !!(perspectives && Object.keys(perspectives).length > 0),
      perspectiveKeys: Object.keys(perspectives || {}),
      hasPlan: !!plan
    });

    let sectionsToUse: InterviewSection[] = [];
    
    // Check if we have stored perspectives
    if (perspectives && Object.keys(perspectives).length > 0) {
      // Use the selected perspective if available, fallback to manager or plan
      const perspectivePlan = perspectives[selectedPerspective] || perspectives.manager || plan;
      console.log('📝 Using perspective plan:', perspectivePlan ? `${perspectivePlan.substring(0, 100)}...` : 'none');
      if (perspectivePlan) {
        sectionsToUse = parseSections(perspectivePlan);
      }
    } else if (plan) {
      // Fallback to original plan if no perspectives are stored
      console.log('📝 Using original plan fallback');
      sectionsToUse = parseSections(plan);
    }
    
    console.log('✅ Setting transformed sections:', sectionsToUse.length, 'sections');
    setTransformedSections(sectionsToUse);
    if (onTransformedSectionsChange) {
      onTransformedSectionsChange(sectionsToUse);
    }
  }, [selectedPerspective, plan, perspectives, onTransformedSectionsChange]);

  // Effect to notify parent about perspective changes and save preference
  useEffect(() => {
    onPerspectiveChange?.(selectedPerspective);
    savePerspectivePreference(selectedPerspective);
  }, [selectedPerspective, analysisId, onPerspectiveChange]);

  // Use transformed sections directly
  const displaySections = transformedSections;

  if (!plan) {
    return (
      <div className="space-y-6">
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-6 text-center">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <ListChecks className="h-6 w-6 text-amber-600" />
              </div>
              <p className="text-sm text-amber-800 font-medium">Interview plan not available</p>
              <p className="text-xs text-amber-600">Complete a resume analysis to generate personalized interview questions</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (displaySections.length === 0 && plan) {
    return (
      <div className="space-y-6">
        <Card className="bg-gradient-to-br from-red-50 to-pink-50 border-red-200">
          <CardContent className="p-6 text-center">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <ShieldX className="h-6 w-6 text-red-600" />
              </div>
              <p className="text-sm text-red-800 font-medium">Could not parse interview plan</p>
              <p className="text-xs text-red-600">The interview plan format may be invalid</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Perspective Selector */}
      <Card className="bg-gradient-to-br from-slate-50 to-slate-100/50 border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center space-x-3 text-lg">
            <Icon className="h-5 w-5 text-slate-600" />
            <span>Interview Perspective</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={selectedPerspective} onValueChange={(value) => {
            const newPerspective = value as InterviewerPerspective;
            setSelectedPerspective(newPerspective);
            onPerspectiveChange?.(newPerspective);
          }}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select interviewer perspective" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(perspectiveConfig).map(([key, config]) => {
                const PerspectiveIcon = config.icon;
                return (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center space-x-3">
                      <PerspectiveIcon className="h-4 w-4" />
                      <span>{config.label}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          
          <div className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-slate-200">
            <Icon className="h-5 w-5 text-slate-500 mt-0.5" />
            <div className="space-y-2">
              <Badge className={currentConfig.color}>
                {currentConfig.label}
              </Badge>
              <p className="text-sm text-slate-600">{currentConfig.description}</p>
              <div className="flex flex-wrap gap-1">
                {currentConfig.focus.map((area, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {area}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Interview Sections */}
      <Accordion type="multiple" className="w-full space-y-3">
        {displaySections.map((section, index) => {
          return (
            <AccordionItem key={index} value={`section-${index}`} className="border rounded-lg overflow-hidden bg-background">
              <AccordionTrigger className="px-4 py-3 hover:bg-accent/50 focus-visible:bg-accent/50 group">
                <div className="flex justify-between items-center w-full">
                  <span className="font-semibold text-foreground text-left mr-4">
                    {section.title}
                  </span>
                  <div className="flex items-center space-x-3">
                    <Badge className={currentConfig.color} variant="outline">
                      {currentConfig.label}
                    </Badge>
                    <div className="flex items-center space-x-1 text-xs text-muted-foreground flex-shrink-0">
                      <Clock className="h-3 w-3" />
                      <span>{section.duration}</span>

                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pt-3 pb-4 border-t bg-background">
                <div className="space-y-4">
                  {/* Perspective-Adapted Questions */}
                  {section.questions.length > 0 && (
                    <div>
                      <h5 className="flex items-center text-sm font-medium text-foreground mb-2">
                        <Icon className="h-4 w-4 mr-2 text-blue-500" /> 
                        Questions ({currentConfig.label} Perspective)

                      </h5>
                      <ul className="list-disc space-y-1.5 pl-9 text-sm text-muted-foreground">
                        {section.questions.map((question, i) => (
                          <li key={i} className="font-medium">
                            {question}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Red Flags */} 
                  {section.redFlags.length > 0 && (
                    <div>
                      <h5 className="flex items-center text-sm font-medium text-destructive mb-2">
                        <ShieldX className="h-4 w-4 mr-2" /> 
                        Watch out for (Red Flags)
                      </h5>
                      <ul className="list-disc space-y-1.5 pl-9 text-sm text-destructive/90">
                        {section.redFlags.map((flag, i) => <li key={i}>{flag}</li>)}
                      </ul>
                    </div>
                  )}
                  {/* Green Flags */}
                  {section.greenFlags.length > 0 && (
                    <div>
                      <h5 className="flex items-center text-sm font-medium text-green-600 mb-2">
                        <ShieldCheck className="h-4 w-4 mr-2" /> 
                        Positive Indicators (Green Flags)
                      </h5>
                      <ul className="list-disc space-y-1.5 pl-9 text-sm text-green-700 dark:text-green-500">
                        {section.greenFlags.map((flag, i) => <li key={i}>{flag}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}